import type { JSX } from 'react';
import { cn } from '@/lib/utils';
import { button, card } from './look';

/** Rows of placeholder bars in the shape of what is coming, not a spinner in a void. */
export function LoadingState({
  rows = 5,
  className,
}: {
  rows?: number;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn(card, 'overflow-hidden', className)} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="border-line flex items-center gap-3.5 border-b px-5 py-3.5 last:border-b-0"
        >
          <div className="skeleton h-6 w-[34px]" />
          <div className="skeleton h-3 flex-1" style={{ maxWidth: `${70 - ((i * 13) % 30)}%` }} />
          <div className="skeleton ml-auto h-3 w-16" />
        </div>
      ))}
    </div>
  );
}

/** What a failed load says, in words that lead to the fix. */
function explain(status: number | undefined): { title: string; body: string } {
  if (status === undefined) {
    return { title: 'The API did not answer', body: 'Check that it is running and reachable.' };
  }
  if (status === 401) {
    return { title: 'Your session has ended', body: 'Sign in again to carry on.' };
  }
  if (status === 403) {
    return { title: 'Your role cannot see this', body: 'Ask an admin if you need access.' };
  }
  if (status >= 500) {
    return {
      title: 'Something behind the API is down',
      body: 'The database or a provider is unreachable. Try again in a moment.',
    };
  }
  return { title: `The API answered ${status}`, body: 'The panel and the API may be out of step.' };
}

export function ErrorState({
  status,
  detail,
  onRetry,
  className,
}: {
  status: number | undefined;
  detail?: string | undefined;
  onRetry: () => void;
  className?: string;
}): JSX.Element {
  const { title, body } = explain(status);
  return (
    <div
      role="alert"
      className={cn(
        'border-bad grid grid-cols-[10px_minmax(0,1fr)_auto] items-center gap-3.5 rounded-xl border px-[18px] py-3.5',
        'bg-[color-mix(in_oklch,var(--bad)_8%,var(--surf))]',
        className,
      )}
    >
      <span className="bg-bad size-2.5 rounded-full" />
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-mute mt-0.5 text-[13px] [overflow-wrap:anywhere]">
          {body}
          {detail !== undefined && <span className="font-mono text-xs"> · {detail}</span>}
        </div>
      </div>
      <button type="button" onClick={onRetry} className={button.small}>
        Retry
      </button>
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  className,
}: {
  title: string;
  body?: string;
  action?: { label: string; run: () => void };
  className?: string;
}): JSX.Element {
  return (
    <div className={cn('text-mute p-10 text-center text-sm', className)}>
      <div className="text-ink font-medium">{title}</div>
      {body !== undefined && <div className="mt-1.5 leading-normal">{body}</div>}
      {action !== undefined && (
        <button type="button" onClick={action.run} className={cn(button.primary, 'mt-4')}>
          {action.label}
        </button>
      )}
    </div>
  );
}
